import React, { useRef, useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ScrollText, ShieldCheck, FileText, Send } from "lucide-react";

/**
 * Sözleşme onay lightbox'ı.
 * Kullanıcı içeriği en alta kaydırana kadar "Okudum ve Onayladım" butonu pasif kalır.
 * onAccept çağrılınca hem KVKK hem Kullanım Koşulları onaylı sayılır (versiyon damgalı).
 * Marketing (açık rıza) opsiyoneldir; onay içinde ayrı bir checkbox olarak sunulur.
 */
export const CONSENT_VERSION = "2026-02-01";

const KVKK_HTML = `
<h3>1. KVKK Aydınlatma Metni</h3>
<p><b>Veri Sorumlusu:</b> SanoStudent (bursluluksinavi.com)</p>
<p>6698 sayılı Kişisel Verilerin Korunması Kanunu (“KVKK”) uyarınca, SanoStudent
tarafından işletilen platformda; kimlik (ad, soyad), iletişim (cep telefonu, e-posta),
öğrenci bilgileri (sınıf, ders, sınav performansı), veli iletişim bilgileri ve platform
kullanım verileriniz aşağıdaki amaçlarla işlenecektir:</p>
<ul>
  <li>Bursluluk sınavı başvurularınızın ilgili okullara iletilmesi</li>
  <li>Deneme sınavı, performans raporu ve tercih danışmanlığı hizmetlerinin sunulması</li>
  <li>SMS/e-posta ile bilgilendirme ve iletişim</li>
  <li>Hesap güvenliği, dolandırıcılık önleme ve yasal yükümlülüklerin yerine getirilmesi</li>
</ul>
<p><b>Aktarım:</b> Bursluluk başvuruları yalnızca sizin seçtiğiniz okullarla; SMS ise
Netgsm sağlayıcısı üzerinden iletilmektedir. Verileriniz yurt dışına aktarılmaz.</p>
<p><b>Haklarınız:</b> KVKK m.11 kapsamında bilgi talep etme, düzeltme, silme ve
itiraz haklarınız vardır. Hesap silme talebiniz için uygulama içinden bir tıkla
hesabınızı silebilir veya <b>kvkk@sanostudent.com</b> adresine yazabilirsiniz.</p>

<h3>2. Kullanım Koşulları</h3>
<p>SanoStudent’a kayıt olarak aşağıdaki maddeleri kabul etmiş sayılırsınız:</p>
<ul>
  <li>18 yaşından küçük öğrenciler, veli bilgilerini eksiksiz girmek zorundadır. Veli
      kişisel verilerinin işlenmesine dair onayı temsilen vermiş kabul edilir.</li>
  <li>Doğru ve güncel bilgi sağlamak kullanıcının sorumluluğundadır. Yanlış beyan
      halinde başvurular reddedilebilir.</li>
  <li>Deneme sınavlarında hile, kopya ve otomatik araç kullanımı yasaktır. İhlal
      halinde hesabınız askıya alınabilir.</li>
  <li>Kredi ve referans sisteminde suistimal tespit edilirse krediler geri alınır.</li>
  <li>Platform içeriği (soru, açıklama, blog yazısı, okul verisi) telif hakkı ile
      korunur. İzinsiz çoğaltılamaz veya dağıtılamaz.</li>
  <li>Hizmet mevcut haliyle sunulur; anlık kesintiler için mazur görülmeliyiz 🙂</li>
</ul>

<h3>3. Öğrenci ve Veli Bilgileri (Kişisel Veri Beyanı)</h3>
<p>Öğrenci hesabı olarak kaydolan kullanıcı, veli iletişim bilgilerini (ad, telefon,
e-posta) veli izniyle beyan ettiğini kabul eder. Veli, çocuğunun hesap açtığından
haberdar edilmeli ve gerekirse veli hesabı üzerinden takip edebileceği bilgilendirilmelidir.
SanoStudent, veli iletişim bilgilerini yalnızca bursluluk süreci ve önemli bildirimler
için kullanır; pazarlama amacıyla kullanılabilmesi ayrıca açık rıza gerektirir.</p>

<h3>4. Çerezler</h3>
<p>Platform, temel oturum çerezleri ve isteğe bağlı analitik çerezler kullanır.
Reddedebilir veya tarayıcı ayarlarınızdan silebilirsiniz.</p>

<h3>5. İletişim</h3>
<p>Her türlü sorunuz için: <b>destek@sanostudent.com</b></p>
<p style="margin-top:24px;font-size:12px;color:#94a3b8">
Sürüm: ${CONSENT_VERSION} · Kabul tarihi hesabınıza kaydedilir.
</p>
`;

export default function ConsentModal({ open, onOpenChange, onAccept, marketingDefault = false }) {
  const scrollRef = useRef(null);
  const [reachedBottom, setReachedBottom] = useState(false);
  const [marketing, setMarketing] = useState(marketingDefault);

  useEffect(() => {
    if (open) {
      setReachedBottom(false);
      setMarketing(marketingDefault);
      // Ensure scroll starts at top
      setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = 0; }, 0);
    }
  }, [open, marketingDefault]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    // Trigger 40px early to feel forgiving
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
      setReachedBottom(true);
    }
  };

  const accept = () => {
    onAccept({
      kvkk: true,
      terms: true,
      marketing,
      version: CONSENT_VERSION,
      accepted_at: new Date().toISOString(),
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 gap-0" data-testid="consent-modal">
        <DialogHeader className="p-6 pb-3 border-b border-border">
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-accent" />
            KVKK Aydınlatma ve Kullanım Koşulları
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Devam etmek için lütfen metnin sonuna kadar okuyun. Onay geçmişi hesabınıza kaydedilir.
          </p>
        </DialogHeader>

        {/* Progress bar */}
        <div className="h-1 bg-muted overflow-hidden">
          <div
            className={`h-full bg-accent transition-all duration-300 ${reachedBottom ? "w-full" : "w-[35%]"}`}
            data-testid="consent-progress"
          />
        </div>

        <div
          ref={scrollRef}
          onScroll={onScroll}
          className="prose prose-sm dark:prose-invert max-w-none overflow-y-auto px-6 py-4 max-h-[55vh] text-sm leading-relaxed"
          data-testid="consent-scroll-area"
          dangerouslySetInnerHTML={{ __html: KVKK_HTML }}
        />

        <div className="px-6 pt-3 pb-2 border-t border-border">
          <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={marketing}
              onChange={(e) => setMarketing(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-accent"
              data-testid="consent-marketing"
            />
            <span>
              <b className="text-foreground">Opsiyonel:</b> SMS/e-posta ile kampanya,
              deneme sınavı hatırlatma ve rehberlik bültenlerinin gönderilmesine açık rıza
              veriyorum. Bu onayı istediğim zaman geri çekebileceğimi biliyorum.
            </span>
          </label>
        </div>

        <div className="p-4 border-t border-border bg-muted/30 flex items-center justify-between gap-3">
          <div className="text-xs text-muted-foreground flex items-center gap-1">
            {reachedBottom ? (
              <><CheckCircle2 className="h-4 w-4 text-success" /> Metnin sonuna ulaştınız</>
            ) : (
              <><ScrollText className="h-4 w-4" /> Butonun aktifleşmesi için metni sonuna kadar kaydırın</>
            )}
          </div>
          <Button
            onClick={accept}
            disabled={!reachedBottom}
            data-testid="consent-accept-btn"
            className="min-w-[220px]"
          >
            <FileText className="h-4 w-4" /> Okudum, Anladım, Onayladım
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
